/*
ol-mapbox-style - Use Mapbox Style objects with OpenLayers
Copyright 2016-present ol-mapbox-style contributors
License: https://raw.githubusercontent.com/openlayers/ol-mapbox-style/master/LICENSE
*/

import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Icon from 'ol/style/Icon';
import Text from 'ol/style/Text';
import Circle from 'ol/style/Circle';
import Point from 'ol/geom/Point';
import derefLayers from '@mapbox/mapbox-gl-style-spec/deref';
import spec from '@mapbox/mapbox-gl-style-spec/reference/latest';
import {
    expression, Color,
    function as fn,
    featureFilter as createFilter
} from '@mapbox/mapbox-gl-style-spec';

import mb2css from 'mapbox-to-css-font';
import {deg2rad, defaultResolutions, getZoomForResolution, wrapText, applyLetterSpacing} from './util';

const isFunction = fn.isFunction;
const convertFunction = fn.convertFunction;
const isExpression = expression.isExpression;
const createPropertyExpression = expression.createPropertyExpression;

const types = {
    'Point': 1,
    'MultiPoint': 1,
    'LineString': 2,
    'MultiLineString': 2,
    'Polygon': 3,
    'MultiPolygon': 3
};

const expressionData = function (rawExpression, propertySpec) {
    const compiledExpression = createPropertyExpression(rawExpression, propertySpec);
    if (compiledExpression.result === 'error') {
        throw new Error(compiledExpression.value.map(err => `${err.key}: ${err.message}`).join(', '));
    }
    return compiledExpression.value;
};

const emptyObj = {};
const zoomObj = {zoom: 0};
const functionCache = {};


/*var spec;

var oReq = new XMLHttpRequest();
oReq.onload = reqListener;
oReq.open("get", "./olms/v8.json", true);
oReq.send();

function reqListener(e) {
    spec = JSON.parse(this.responseText);
}*/


/**
 * @private
 * @param {Object} layer Gl object layer.
 * @param {string} layoutOrPaint 'layout' or 'paint'.
 * @param {string} property Feature property.
 * @param {number} zoom Zoom.
 * @param {Object} feature Gl feature.
 * @return {?} Value.
 */
export function getValue(layer, layoutOrPaint, property, zoom, feature) {
    const layerId = layer.id;
    if (!functionCache[layerId]) {
        functionCache[layerId] = {};
    }
    const functions = functionCache[layerId];
    if (!functions[property]) {
        let value = (layer[layoutOrPaint] || emptyObj)[property];


        //const propertySpec = v8p[`${layoutOrPaint}_${layer.type}`][property];
       const propertySpec = spec[`${layoutOrPaint}_${layer.type}`][property];
        if (value === undefined) {
            value = propertySpec.default;
        }
        let isExpr = isExpression((value));
        if (!isExpr && isFunction(value)) {
            value = convertFunction(value, propertySpec);
            isExpr = true;
        }
        if (isExpr) {
            const compiledExpression = expressionData(value, propertySpec);
            functions[property] = compiledExpression.evaluate.bind(compiledExpression);
        } else {
            if (propertySpec.type == 'color') {
                value = Color.parse(value);
            }
            functions[property] = function () {
                return value;
            };
        }
    }
    zoomObj.zoom = zoom;
    return functions[property](zoomObj, feature);
}

const filterCache = {};

function evaluateFilter(layerId, filter, feature, zoom) {
    if (!(layerId in filterCache)) {
        filterCache[layerId] = createFilter(filter);
    }
    zoomObj.zoom = zoom;
    return filterCache[layerId](zoomObj, feature);
}

function colorWithOpacity(color, opacity) {
    if (color) {
        if (color.a === 0 || opacity === 0) {
            return undefined;
        }
        const a = color.a;
        opacity = opacity === undefined ? 1 : opacity;
        return 'rgba(' + Math.round(color.r * 255 / a) + ',' + Math.round(color.g * 255 / a) +
            ',' + Math.round(color.b * 255 / a) + ',' + (a * opacity) + ')';
    }
    return color;
}

const templateRegEx = /^([^]*)\{(.*)\}([^]*)$/;

function fromTemplate(text, properties) {
    let parts;
    do {
        parts = text.match(templateRegEx);
        if (parts) {
            const value = properties[parts[2]] || '';
            text = parts[1] + value + parts[3];
        }
    } while (parts);
    return text;
}


export default function (glStyle, sources, resolutions = defaultResolutions, spriteData, spriteImageUrl, getFonts) {

    if (typeof glStyle == 'string') {
        glStyle = JSON.parse(glStyle);
    }
    if (glStyle.version != 8) {
        throw new Error('glStyle version 8 required.');
    }

    let spriteImage, spriteImgSize;
    if (spriteImageUrl) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () {
            spriteImage = img;
            spriteImgSize = [img.width, img.height];
            img.onload = null;
        };
        img.src = spriteImageUrl;
    }


    const allLayers = derefLayers(glStyle.layers);

    const layersBySourceLayer = {};
    const mapboxLayers = [];
    let mapboxSource;
    for (let i = 0, ii = allLayers.length; i < ii; ++i) {
        const layer = allLayers[i];
        const layerId = layer.id;
        for (let k = 0; k < sources.length; k++) {
            const source = sources[k];
            if (typeof source == 'string' && layer.source == source ||
                source.indexOf(layerId) !== -1) {
                const sourceLayer = layer['source-layer'];
                if (!mapboxSource) {
                    mapboxSource = layer.source;
                    const source = glStyle.sources[mapboxSource];
                    if (!source) {
                        continue;
                    }
                    const type = source.type;
                    if (type !== 'vector' && type !== 'geojson') {
                        throw new Error(`Source "${mapboxSource}" is not of type "vector" or "geojson", but "${type}"`);
                    }
                }
                let layers = layersBySourceLayer[sourceLayer];
                if (!layers) {
                    layers = layersBySourceLayer[sourceLayer] = [];
                }
                layers.push({
                    layer: layer,
                    index: i
                });
                mapboxLayers.push(layerId);
                // TODO revisit when diffing gets added
            }
            delete functionCache[layerId];
            delete filterCache[layerId];
        }
    }

    const textHalo = new Stroke();
    const textColor = new Fill();

    const iconImageCache = {};
    const patternCache = {};
    const styles = [];

    const styleFunction = function (feature, level) {
        const properties = feature.getProperties();

        const layers = layersBySourceLayer[properties.layer];
        if (!layers) {
            return;
        }
        // let zoom = resolutions.indexOf(resolution);
        // if (zoom == -1) {
        //   zoom = getZoomForResolution(resolution, resolutions);
        // }
        const zoom = level + 1;
        const type = types[feature.getGeometry().getType()];
        const f = {
            properties: properties,
            type: type
        };
        let stylesLength = -1;
        const resolution = resolutions[zoom];

        for (let i = 0, ii = layers.length; i < ii; ++i) {
            const layerData = layers[i];
            const layer = layerData.layer;
            const layerId = layer.id;

            const layout = layer.layout || emptyObj;
            const paint = layer.paint || emptyObj;
            if (layout.visibility === 'none' || ('minzoom' in layer && zoom < layer.minzoom) ||
                ('maxzoom' in layer && zoom >= layer.maxzoom)) {
                continue;
            }
            const filter = layer.filter;
            if (!filter || evaluateFilter(layerId, filter, f, zoom)) {
                let color, opacity, fill, stroke, strokeColor, style;
                const index = layerData.index;
                if (type == 3 && layer.type == 'fill') {
                    opacity = getValue(layer, 'paint', 'fill-opacity', zoom, f);
                    if ('fill-pattern' in paint) {
                        const fillIcon = getValue(layer, 'paint', 'fill-pattern', zoom, f);
                        if (fillIcon) {
                            const icon = fromTemplate(fillIcon, properties);
                            if (spriteImage && spriteData && spriteData[icon]) {
                                ++stylesLength;
                                style = styles[stylesLength];
                                if (!style || !style.getFill() || style.getStroke() || style.getText()) {
                                    style = styles[stylesLength] = new Style({
                                        fill: new Fill()
                                    });
                                }
                                fill = style.getFill();
                                style.setZIndex(index);
                                const icon_cache_key = icon + '.' + opacity;
                                let pattern = patternCache[icon_cache_key];
                                if (!pattern) {
                                    const spriteImageData = spriteData[icon];
                                    const canvas = document.createElement('canvas');
                                    canvas.width = spriteImageData.width;
                                    canvas.height = spriteImageData.height;
                                    const ctx = canvas.getContext('2d');
                                    ctx.globalAlpha = opacity;
                                    ctx.drawImage(
                                        spriteImage,
                                        spriteImageData.x,
                                        spriteImageData.y,
                                        spriteImageData.width,
                                        spriteImageData.height,
                                        0,
                                        0,
                                        spriteImageData.width,
                                        spriteImageData.height
                                    );
                                    pattern = ctx.createPattern(canvas, 'repeat');
                                    patternCache[icon_cache_key] = pattern;
                                }
                                fill.setColor(pattern);
                            }
                        }
                    } else {
                        color = colorWithOpacity(getValue(layer, 'paint', 'fill-color', zoom, f), opacity);
                        if (color) {
                            if ('fill-outline-color' in paint) {
                                strokeColor = colorWithOpacity(getValue(layer, 'paint', 'fill-outline-color', zoom, f), opacity);
                            }
                            if (!strokeColor) {
                                strokeColor = color;
                            }
                            ++stylesLength;
                            style = styles[stylesLength];
                            if (!style || !(style.getFill() && style.getStroke()) || style.getText()) {
                                style = styles[stylesLength] = new Style({
                                    fill: new Fill(),
                                    stroke: new Stroke()
                                });
                            }
                            fill = style.getFill();
                            fill.setColor(color);
                            stroke = style.getStroke();
                            stroke.setColor(strokeColor);
                            stroke.setWidth(1);
                            style.setZIndex(index);
                        }
                    }
                }
                if (type != 1 && layer.type == 'line') {
                    color = !('line-pattern' in paint) && 'line-color' in paint ?
                        colorWithOpacity(getValue(layer, 'paint', 'line-color', zoom, f), getValue(layer, 'paint', 'line-opacity', zoom, f)) :
                        undefined;
                    const width = getValue(layer, 'paint', 'line-width', zoom, f);
                    if (color && width > 0) {
                        ++stylesLength;
                        style = styles[stylesLength];
                        if (!style || !style.getStroke() || style.getFill() || style.getText()) {
                            style = styles[stylesLength] = new Style({
                                stroke: new Stroke()
                            });
                        }
                        stroke = style.getStroke();
                        stroke.setLineCap(getValue(layer, 'layout', 'line-cap', zoom, f));
                        stroke.setLineJoin(getValue(layer, 'layout', 'line-join', zoom, f));
                        stroke.setMiterLimit(getValue(layer, 'layout', 'line-miter-limit', zoom, f));
                        stroke.setColor(color);
                        stroke.setWidth(width);
                        stroke.setLineDash(paint['line-dasharray'] ?
                            getValue(layer, 'paint', 'line-dasharray', zoom, f).map(function (x) {
                                return x * width;
                            }) : null);
                        style.setZIndex(index);
                    }
                }

                let hasImage = false;
                let text = null;
                let placementAngle = 0;
                let icon, iconImg, skipLabel;
                if ((type == 1 || type == 2) && 'icon-image' in layout) {
                    const iconImage = getValue(layer, 'layout', 'icon-image', zoom, f);
                    if (iconImage) {
                        icon = fromTemplate(iconImage, properties);
                        let styleGeom = undefined;
                        if (spriteImage && spriteData && spriteData[icon]) {
                            const iconRotationAlignment = getValue(layer, 'layout', 'icon-rotation-alignment', zoom, f);
                            if (type == 2) {
                                const geom = feature.getGeometry();
                                // ol package and ol-debug.js only
                                if (geom.getFlatMidpoint) {
                                    const extent = geom.getExtent();
                                    // const size = Math.sqrt(Math.max(
                                    //     Math.pow(((extent[2] - extent[0])/8) / resolution, 2),
                                    //     Math.pow(((extent[3] - extent[1])/8) / resolution, 2))
                                    // );

                                    const size = Math.sqrt(Math.max(
                                        Math.pow(((extent[2] - extent[0]) / 8), 2),
                                        Math.pow(((extent[3] - extent[1]) / 8), 2))
                                    );
                                    if (size > (150 * 1)) {
                                        //FIXME Do not hard-code a size of 150
                                        const midpoint = geom.getFlatMidpoint();
                                        styleGeom = new Point(midpoint);
                                        const placement = getValue(layer, 'layout', 'symbol-placement', zoom, f);
                                        if (placement === 'line' && iconRotationAlignment === 'map') {
                                            const stride = geom.getStride();
                                            const coordinates = geom.getFlatCoordinates();
                                            for (let i = 0, ii = coordinates.length - stride; i < ii; i += stride) {
                                                const x1 = coordinates[i];
                                                const y1 = coordinates[i + 1];
                                                const x2 = coordinates[i + stride];
                                                const y2 = coordinates[i + stride + 1];
                                                const minX = Math.min(x1, x2);
                                                const minY = Math.min(y1, y2);
                                                const maxX = Math.max(x1, x2);
                                                const maxY = Math.max(y1, y2);
                                                if (midpoint[0] >= minX && midpoint[0] <= maxX &&
                                                    midpoint[1] >= minY && midpoint[1] <= maxY) {
                                                    placementAngle = Math.atan2(y1 - y2, x2 - x1);
                                                    break;
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            if (type !== 2 || styleGeom) {
                                ++stylesLength;
                                style = styles[stylesLength];
                                if (!style || !style.getImage() || style.getFill() || style.getStroke()) {
                                    style = styles[stylesLength] = new Style();
                                }
                                style.setGeometry(styleGeom);
                                const iconSize = getValue(layer, 'layout', 'icon-size', zoom, f);
                                const iconColor = paint['icon-color'] !== undefined ? getValue(layer, 'paint', 'icon-color', zoom, f) : null;
                                let icon_cache_key = icon + '.' + iconSize;
                                if (iconColor !== null) {
                                    icon_cache_key += '.' + iconColor;
                                }
                                iconImg = iconImageCache[icon_cache_key];
                                if (!iconImg) {
                                    const spriteImageData = spriteData[icon];
                                    if (iconColor !== null) {
                                        // cut out the sprite and color it
                                        color = colorWithOpacity(iconColor, 1);
                                        const canvas = document.createElement('canvas');
                                        canvas.width = spriteImageData.width;
                                        canvas.height = spriteImageData.height;
                                        const ctx = canvas.getContext('2d');
                                        ctx.drawImage(
                                            spriteImage,
                                            spriteImageData.x,
                                            spriteImageData.y,
                                            spriteImageData.width,
                                            spriteImageData.height,
                                            0,
                                            0,
                                            spriteImageData.width,
                                            spriteImageData.height
                                        );
                                        const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
                                        for (let c = 0, cc = data.data.length; c < cc; c += 4) {
                                            data.data[c] = color[0];
                                            data.data[c + 1] = color[1];
                                            data.data[c + 2] = color[2];
                                        }
                                        ctx.putImageData(data, 0, 0);
                                        iconImg = iconImageCache[icon_cache_key] = new Icon({
                                            img: canvas,
                                            imgSize: [canvas.width, canvas.height],
                                            scale: iconSize / spriteImageData.pixelRatio
                                        });
                                    } else {
                                        iconImg = iconImageCache[icon_cache_key] = new Icon({
                                            img: spriteImage,
                                            imgSize: spriteImgSize,
                                            size: [spriteImageData.width, spriteImageData.height],
                                            offset: [spriteImageData.x, spriteImageData.y],
                                            rotateWithView: iconRotationAlignment === 'map',
                                            scale: iconSize / spriteImageData.pixelRatio
                                        });
                                    }
                                }
                                iconImg.setRotation(placementAngle + deg2rad(getValue(layer, 'layout', 'icon-rotate', zoom, f)));
                                iconImg.setOpacity(getValue(layer, 'paint', 'icon-opacity', zoom, f));
                                style.setImage(iconImg);
                                text = style.getText();
                                style.setText(undefined);
                                style.setZIndex(99999 - index);
                                hasImage = true;
                                skipLabel = false;
                            } else {
                                skipLabel = true;
                            }
                        }
                    }
                }

                if (type == 1 && 'circle-radius' in paint) {
                    ++stylesLength;
                    style = styles[stylesLength];
                    if (!style || !style.getImage() || style.getFill() || style.getStroke()) {
                        style = styles[stylesLength] = new Style();
                    }
                    const circleRadius = getValue(layer, 'paint', 'circle-radius', zoom, f);
                    const circleStrokeColor = colorWithOpacity(getValue(layer, 'paint', 'circle-stroke-color', zoom, f), getValue(layer, 'paint', 'circle-stroke-opacity', zoom, f));
                    const circleColor = colorWithOpacity(getValue(layer, 'paint', 'circle-color', zoom, f), getValue(layer, 'paint', 'circle-opacity', zoom, f));
                    const circleStrokeWidth = getValue(layer, 'paint', 'circle-stroke-width', zoom, f);
                    const cache_key = circleRadius + '.' + circleStrokeColor + '.' +
                        circleColor + '.' + circleStrokeWidth;
                    iconImg = iconImageCache[cache_key];
                    if (!iconImg) {
                        iconImg = iconImageCache[cache_key] = new Circle({
                            radius: circleRadius,
                            stroke: circleStrokeColor && circleStrokeWidth > 0 ? new Stroke({
                                width: circleStrokeWidth,
                                color: circleStrokeColor
                            }) : undefined,
                            fill: circleColor ? new Fill({
                                color: circleColor
                            }) : undefined
                        });
                    }
                    style.setImage(iconImg);
                    text = style.getText();
                    style.setText(undefined);
                    style.setGeometry(undefined);
                    style.setZIndex(99999 - index);
                    hasImage = true;
                }

                let label;
                if ('text-field' in layout) {
                    const textField = getValue(layer, 'layout', 'text-field', zoom, f).toString();
                    label = fromTemplate(textField, properties);
                    opacity = getValue(layer, 'paint', 'text-opacity', zoom, f);
                }
                if (label && opacity && !skipLabel) {
                    if (!hasImage) {
                        ++stylesLength;
                        style = styles[stylesLength];
                        if (!style || !style.getText() || style.getFill() || style.getStroke()) {
                            style = styles[stylesLength] = new Style();
                        }
                        style.setImage(undefined);
                        style.setGeometry(undefined);
                    }
                    if (!style.getText()) {
                        style.setText(text || new Text({
                            padding: [2, 2, 2, 2]
                        }));
                    }
                    text = style.getText();
                    const textSize = getValue(layer, 'layout', 'text-size', zoom, f);
                    const fontArray = getValue(layer, 'layout', 'text-font', zoom, f);
                    const textLineHeight = getValue(layer, 'layout', 'text-line-height', zoom, f);
                    const font = mb2css(getFonts ? getFonts(fontArray) : fontArray, textSize, textLineHeight);
                    const textTransform = layout['text-transform'];
                    if (textTransform == 'uppercase') {
                        label = label.toUpperCase();
                    } else if (textTransform == 'lowercase') {
                        label = label.toLowerCase();
                    }
                    const maxTextWidth = getValue(layer, 'layout', 'text-max-width', zoom, f);
                    const letterSpacing = getValue(layer, 'layout', 'text-letter-spacing', zoom, f);
                    const wrappedLabel = type == 2 ? applyLetterSpacing(label, letterSpacing) : wrapText(label, font, maxTextWidth, letterSpacing);
                    text.setText(wrappedLabel);
                    text.setFont(font);
                    text.setRotation(deg2rad(getValue(layer, 'layout', 'text-rotate', zoom, f)));
                    const textAnchor = getValue(layer, 'layout', 'text-anchor', zoom, f);
                    const placement = (hasImage || type == 1) ? 'point' : getValue(layer, 'layout', 'symbol-placement', zoom, f);
                    text.setPlacement(placement);
                    let textHaloWidth = getValue(layer, 'paint', 'text-halo-width', zoom, f);
                    const textOffset = getValue(layer, 'layout', 'text-offset', zoom, f);
                    const textTranslate = getValue(layer, 'paint', 'text-translate', zoom, f);
                    // Text offset has to take halo width and line height into account
                    let vOffset = 0;
                    let hOffset = 0;
                    if (placement == 'point') {
                        let textAlign = 'center';
                        if (textAnchor.indexOf('left') !== -1) {
                            textAlign = 'left';
                            hOffset = textHaloWidth;
                        } else if (textAnchor.indexOf('right') !== -1) {
                            textAlign = 'right';
                            hOffset = -textHaloWidth;
                        }
                        text.setTextAlign(textAlign);
                    } else {
                        text.setMaxAngle(deg2rad(getValue(layer, 'layout', 'text-max-angle', zoom, f)));
                        text.setTextAlign();
                    }
                    let textBaseline = 'middle';
                    if (textAnchor.indexOf('bottom') == 0) {
                        textBaseline = 'bottom';
                        vOffset = -textHaloWidth - (0.5 * (textLineHeight - 1)) * textSize;
                    } else if (textAnchor.indexOf('top') == 0) {
                        textBaseline = 'top';
                        vOffset = textHaloWidth + (0.5 * (textLineHeight - 1)) * textSize;
                    }
                    text.setTextBaseline(textBaseline);
                    text.setOffsetX(textOffset[0] * textSize + hOffset + textTranslate[0]);
                    text.setOffsetY(textOffset[1] * textSize + vOffset + textTranslate[1]);
                    textColor.setColor(colorWithOpacity(getValue(layer, 'paint', 'text-color', zoom, f), opacity));
                    text.setFill(textColor);
                    const haloColor = colorWithOpacity(getValue(layer, 'paint', 'text-halo-color', zoom, f), opacity);
                    if (haloColor) {
                        textHalo.setColor(haloColor);
                        // spec here : https://docs.mapbox.com/mapbox-gl-js/style-spec/#paint-symbol-text-halo-width
                        // Halo width must be doubled because it is applied around the center of the text outline
                        textHaloWidth *= 2;
                        // 1/4 of text size (spec) x 2
                        const halfTextSize = 0.5 * textSize;
                        textHalo.setWidth(textHaloWidth <= halfTextSize ? textHaloWidth : halfTextSize);
                        text.setStroke(textHalo);
                    } else {
                        text.setStroke(undefined);
                    }
                    const textPadding = getValue(layer, 'layout', 'text-padding', zoom, f);
                    const padding = text.getPadding();
                    if (textPadding !== padding[0]) {
                        padding[0] = padding[1] = padding[2] = padding[3] = textPadding;
                    }
                    style.setZIndex(99999 - index);
                }
            }
        }


        if (stylesLength > -1) {
            styles.length = stylesLength + 1;
            return styles;
        }

        /*这里一定要返回空数组，返回空数组没有切片的地方就不显示了，否则将旧切片放大失真显示，原来没有返回空数组，这里很坑*/
        return []
    };

    // olLayer.setStyle(styleFunction);
    // olLayer.set('mapbox-source', mapboxSource);
    // olLayer.set('mapbox-layers', mapboxLayers);
    return styleFunction;
}

export {
    colorWithOpacity as _colorWithOpacity,
    filterCache as _filterCache,
    evaluateFilter as _evaluateFilter,
    fromTemplate as _fromTemplate,
    getValue as _getValue,
    functionCache as _functionCache
};
